import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Container, PillButton, SectionHeading } from "@/components/common/ui";
import { StepList } from "@/components/common/StepList";
import { MortgageCalculator } from "@/components/calculators/MortgageCalculator";
import { EnquiryForm } from "@/components/forms/EnquiryForm";
import { RentVsBuyCalculator } from "@/components/calculators/RentVsBuyCalculator";
import { AssistanceScreener } from "@/components/tools/AssistanceScreener";
import { OnboardingQuestionnaire } from "@/components/tools/OnboardingQuestionnaire";
import { SchedulingActions } from "@/components/tools/SchedulingActions";
import { BUYER_STEPS, IMAGES } from "@/lib/site";

const GLOSSARY = [
  ["Pre-approval", "A lender's written estimate of how much you can borrow after reviewing your income, assets and credit."],
  ["Debt-to-income ratio (DTI)", "Your monthly debt payments divided by your gross monthly income; lenders use it to judge affordability."],
  ["Earnest money", "A good-faith deposit made with your offer, credited toward your purchase at closing."],
  ["Escrow", "The period after an offer is accepted, when funds and documents are held by a neutral party until closing."],
  ["Contingency", "A condition in the contract (inspection, appraisal, financing) that must be met for the sale to proceed."],
  ["Appraisal", "An independent estimate of the home's value ordered by your lender."],
  ["PMI", "Private mortgage insurance, typically required on conventional loans with less than 20% down. Not required on V.A. loans."],
  ["V.A. funding fee", "A one-time fee on V.A. loans that varies with down payment and prior use; some veterans are exempt."],
  ["Closing costs", "Fees for the loan, title and recording paid at closing, separate from your down payment."],
  ["FHA 203K", "An FHA loan that rolls the cost of renovations into your mortgage."],
];

export default function BuyersPage() {
  return (
    <div data-testid="buyers-page">
      <section className="border-b border-neutral-200">
        <Container className="grid gap-10 py-14 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:py-20">
          <div>
            <p className="eyebrow">Virginia Home Buyers Guide</p>
            <h1 className="mt-4 font-display text-5xl font-normal leading-[1.05] sm:text-6xl">Buying a home in Richmond & Hampton Roads.</h1>
            <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-neutral-600">
              Buying a house requires a lot of time and effort, but is extremely worth it in the long run. This area is often referred to as a megaregion — Richmond is the state capital and Hampton Roads is about 100 miles south, with plenty of incredible neighborhoods, schools, amenities, and entertainment in between.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <PillButton to="/search" testId="buyers-search">Search homes</PillButton>
              <PillButton href="#pre-approval" tone="outline" arrow={false} testId="buyers-preapproval">Get pre-approved</PillButton>
            </div>
          </div>
          <img src={IMAGES.kitchen} alt="Bright kitchen in a Virginia home" className="aspect-[4/3] w-full rounded-md object-cover" />
        </Container>
      </section>

      <section className="py-16 sm:py-20">
        <Container className="grid gap-12 lg:grid-cols-[1fr_1.6fr]">
          <div className="lg:sticky lg:top-24 lg:self-start">
            <SectionHeading eyebrow="Step by step" title="Buying a home in VA" subtitle="Whether you're a first-time homebuyer, relocating from out of state, or on PCS orders — here's how it works." />
          </div>
          <StepList steps={BUYER_STEPS} testId="buyer-steps" />
        </Container>
      </section>

      <section id="calculator" className="scroll-mt-20 bg-mist py-16 sm:py-20">
        <Container>
          <SectionHeading eyebrow="Mortgage calculator" title="What will it cost each month?" subtitle="Compare V.A., conventional and FHA payments side by side." />
          <div className="mt-8"><MortgageCalculator defaultLoan="conventional" showBah testId="buyers-calculator" /></div>
        </Container>
      </section>

      <section className="py-16 sm:py-20" data-testid="buyer-rent-vs-buy-section"><Container><SectionHeading eyebrow="Move decision" title="Rent now or buy now?" subtitle="Compare your likely rent path with an ownership payment and a simple equity projection." /><div className="mt-8"><RentVsBuyCalculator testId="buyers-rent-vs-buy" /></div></Container></section>

      <section className="bg-mist py-16 sm:py-20" data-testid="buyer-assistance-section"><Container className="grid gap-10 lg:grid-cols-2 lg:items-start"><AssistanceScreener testId="buyers-assistance-screener" /><div className="lg:pt-4"><SectionHeading eyebrow="Meet the team prepared" title="Your plan, before the first tour." subtitle="Share your goals once. We’ll make the first conversation focused on the homes, financing path and timing that fit." /><div className="mt-8"><OnboardingQuestionnaire mode="buyer" testId="buyer-onboarding" /></div><div className="mt-6"><SchedulingActions audience="buyer" testId="buyer-scheduling" /></div></div></Container></section>

      <section id="pre-approval" className="scroll-mt-20 py-16 sm:py-20">
        <Container className="grid gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="Mortgage pre-approval & lenders" title="Get matched with a local lender"
              subtitle="We help educate our clients in the latest mortgage programs — V.A., Virginia Housing Grant, FHA 203K, and stated income and self-employed programs." />
            <div className="mt-8 rounded-md border border-neutral-200 p-6"><EnquiryForm kind="pre-approval" submitLabel="Connect me with a lender" testIdPrefix="preapproval" messagePlaceholder="Price range, loan type, timing…" /></div>
          </div>
          <div>
            <SectionHeading eyebrow="Financial terms glossary" title="Words you'll hear" />
            <Accordion type="single" collapsible className="mt-6" data-testid="glossary">
              {GLOSSARY.map(([t, d], i) => (
                <AccordionItem key={t} value={`g-${i}`}>
                  <AccordionTrigger className="text-left text-[17px] font-normal hover:no-underline" data-testid={`glossary-${i}`}>{t}</AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-neutral-600">{d}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Container>
      </section>
    </div>
  );
}
